<template>
  <v-container fluid>

    <loading v-model="loading" />

    <v-snackbar v-model="msg.show" value="true" right top multi-line :color="msg.type" :timeout="msg.time">
      {{ msg.text }}
      <v-btn dark flat @click="msg.show = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-card v-if="!isImported">
      <v-toolbar flat color="transparent">
        <v-toolbar-title>{{ $t('Products available') }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-tooltip bottom>
          <v-switch slot="activator"
            :value="isFilterQuantityZero"
            @change="toggleFilterQuantityZero"
            :label="$t('Include zero qty')"
            hide-details
            class="mx-1"
          ></v-switch>
          <span>{{ $t('Include products with zero qty') }}</span>
        </v-tooltip>

        <v-select
          :label="$t('Warehouse')"
          :items="wmsStations"
          item-value="wms_station_code"
          item-text="tp_station_name"
          v-model="stationFilter"
          @change="getItems()"
          clearable
          @click:clear="stationFilter = null; getItems()"
          hide-details
          full-width background-color="grey lighten-4"
          class="mx-1"
        ></v-select>

        <v-spacer class="mx-1"></v-spacer>

        <div v-for="header in headersWithFilters" :key="header.text" class="mx-1">
          <template v-if="header.value === 'exp_date'">
            <v-tooltip bottom>
              <v-input-date-range-picker
                slot="activator"
                v-model="header.filter"
                @input="getItems"
                :label="header.text"
                :max="null"
              ></v-input-date-range-picker>
              <span>{{ header.text }}</span>
            </v-tooltip>
          </template>

          <template v-else>
            <v-tooltip bottom>
              <v-text-field slot="activator"
                :label="header.text"
                v-model="header.filter"
                @keyup.enter="getItems()"

                append-icon="search"
                @click:append="getItems()"

                clearable
                @click:clear="header.filter = null; getItems()"

                hide-details
                full-width background-color="grey lighten-4"
              ></v-text-field>
              <span>{{ header.text }}</span>
            </v-tooltip>
          </template>
        </div>

        <v-spacer class="mx-1"></v-spacer>

        <v-tooltip bottom>
          <v-btn slot="activator" icon flat @click="clearFilters" color="grey darken-1" :disabled="!filtersOn || !Object.keys(filtersOn).length">
            <v-icon>search_off</v-icon>
          </v-btn>
          <span>{{ $t('Clear all filters') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
         <v-btn icon slot="activator" @click="exportExcel" v-shortkey="['ctrl', 'alt', 'e']" @shortkey="exportExcel">
          <v-icon class="text--secondary">fa-file-excel</v-icon>
         </v-btn>
         <span>{{ $t('Export to Excel') }}</span>
        </v-tooltip>

        <v-divider vertical inset></v-divider>

        <v-tooltip bottom><!-- TODO: extract to component -->
          <v-menu slot="activator"
            :close-on-content-click="false"
            :nudge-width="200"
            offset-x
            left
          >
            <v-btn icon slot="activator">
              <v-icon v-if="headers.some(header => !header.show)" class="text--secondary">settings_suggest</v-icon>
              <v-icon v-else class="text--secondary">settings</v-icon>
            </v-btn>

            <v-card>
              <v-list>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('Table settings') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ $t('Select visible columns') }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
              <v-divider></v-divider>
              <v-list dense>
                <v-list-tile v-for="(header,key) in headers.filter(header => header.value !== 'actions')" :key="'col-'+key" @click.prevent="columnToggle(header)">
                  <v-list-tile-action>
                    <v-switch :value="header.show" :input-value="header.show" color="primary" :disabled="columnHideCheck(header)"></v-switch>
                  </v-list-tile-action>
                  <v-list-tile-title>{{ header.text }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-card>
          </v-menu>
          <span>{{ $t('Table settings') }}</span>
        </v-tooltip>
      </v-toolbar>

      <v-data-table
        :headers="headersShown"
        :items="items"
        :pagination.sync="pagination"
        :total-items="itemsTotal"
        item-key="id"
        :loading="loading"
        :rows-per-page-items="[5, 10, 25, 50]"
        :rows-per-page-text="$t('Rows per page')"
        :no-data-text="$t('No data')"
        :no-results-text="$t('No results')"
        class="elevation-1"
      >
        <template v-slot:progress>
          <v-progress-linear indeterminate height="2" style="position: absolute; width: 100%; z-index: 10;"></v-progress-linear>
        </template>
        <template v-slot:headers="props">
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="[
                'column sortable',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : '',
                'col-' + header.value]"
              class="px-2"
            >
              <template>
                <v-tooltip bottom>
                  <div slot="activator" @click="changeSort(header)">
                    <v-icon small v-if="header.sortable">arrow_upward</v-icon>
                    {{ header.text }}
                  </div>
                  <span>{{ header.text }}</span>
                </v-tooltip>
              </template>
            </th>
            <th class="text-xs-right blue lighten-3 white--text font-weight-bold col-input_quantity">
              {{ $t('Order delivery notes') }}<br/>
              {{ $t('Qty') }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <tr :active="props.item.quantityInput > 0"
            :style="props.item.free_quantity > 0 ? '' : 'opacity: 0.3'"
            :class="!!props.item.grouped ? 'orange lighten-4' : ''"
          >
            <td
              v-for="header in headersShown"
              :key="header.value"
              :class="['text-xs-' + header.align, header.value.includes('quantity') ? 'blue lighten-5' : '']"
            >
              <template v-if="header.value === 'code'">
                <v-badge :value="!!props.item.grouped" color="orange">
                  <template v-slot:badge>
                    <span>{{ props.item.grouped }}</span>
                  </template>
                  {{ props.item.code }}
                </v-badge>
              </template>
              <template v-else-if="header.value === 'unit_code'">
                {{ props.item.unit_code }}
              </template>
              <template v-else-if="header.value === 'wms_station_code'">
                {{ props.item.tp_station_name }}
              </template>
              <template v-else-if="header.value === 'weight'">
                {{ floatTrim(props.item.weight) }}
              </template>
              <template v-else-if="header.value === 'exp_date'">
                {{ printDateTime(props.item.exp_date, 1) }}
              </template>
              <template v-else-if="header.value === 'blocked_quantity'">
                <v-badge :value="!!props.item.unmatched" color="red">
                  <template v-slot:badge>
                    <span>!</span>
                  </template>
                  {{ props.item.blocked_quantity }}
                </v-badge>
              </template>
              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="text-xs-right blue lighten-3 px-1">
              <v-text-field
                :value="props.item.quantityInput"
                @input="props.item.quantityInput = Number($event); selectItem(props.item)"
                type="number"
                solo flat background-color="transparent"
                class="input-right"
                @click.stop
                :disabled="!props.item.free_quantity"
                :hide-details="rules.isQuantityLimits(props.item.quantityInput, props.item.free_quantity)"
                :rules="[rules.quantityZero(props.item.quantityInput), rules.quantityMax(props.item.quantityInput, props.item.free_quantity)]"
                :messages="rules.msgQuantityLimitsBlocked(props.item)"
              ></v-text-field>
            </td>
          </tr>
        </template>
        <template v-slot:actions-prepend>
          <div class="layout align-center">
            <v-chip small label class="mr-4" color="orange" v-if="itemsGroupedCount">{{ $t('Unified rows') }}: {{ itemsGroupedCount }}</v-chip>
            <v-chip small label dark class="mr-4" color="red" v-if="isItemsUnmatched">! {{ $t('Possible missmatch in Qty Locked') }}</v-chip>
            <v-spacer style="width: 50px"></v-spacer>
            <v-pagination v-model="pagination.page" circle :length="pages" total-visible="7" class="pagination"></v-pagination>
          </div>
        </template>
      </v-data-table>



      <v-toolbar flat color="transparent" class="pt-4">
        <v-toolbar-title>{{ $t('Selected for delivery note') }} <span v-if="selectedItems.length">({{ selectedItems[0].tp_station_name }})</span></v-toolbar-title>

        <v-tooltip bottom>
          <v-btn slot="activator" flat @click="checkReserved" :color="!!checkResponse ? 'success' : 'info'" :disabled="!selectedItems.length">
            <v-icon left v-if="!!checkResponse">library_add_check</v-icon>
            <v-icon left v-else>quiz</v-icon>
            {{ $t('Check available') }}
          </v-btn>
          <span>{{ $t('Check selected if available for order (check order state)') }}</span>
        </v-tooltip>

        <v-spacer></v-spacer>

        <v-menu
          nudge-right
          :left="!$vuetify.breakpoint.xsOnly"
          :min-width="$vuetify.breakpoint.xsOnly ? '96%' : ''"
          :close-on-content-click="false"
          origin="top right"
          transition="scale-transition"
          v-model="customer.isDialog"
        >
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn v-on="{ ...tooltip, ...menu }" v-bind="attrs" outline color="success" :disabled="!selectedItems.length">
                  <v-icon left>expand_more</v-icon>
                  {{ $t('Order delivery notes') }}...
                </v-btn>
              </template>
              <span>{{ $t("Proceed to deliver selected notes: set props for customer and shipment") }}</span>
            </v-tooltip>
          </template>
          <v-card max-width="640px">
            <v-form ref="customerForm" v-model="customer.isFormValid">
            <v-container grid-list-md fluid class="pa-3">
              <v-layout>
                <v-flex>
                  <VSuggestWmsCustomer :label="$t('Customer')" box @input="wmsCustomerSuggesterChange"></VSuggestWmsCustomer>
                </v-flex>
              </v-layout>
              <v-layout wrap align-center>
                <v-flex class="sm5">
                  <v-text-field v-model="customer.item.code" :label="$t('Code')" :disabled="!isNewWmsOrTpCustomer"
                    :rules="[rules.isValue]"
                    :messages="customer.item.metadata.generated_code && customer.item.code === customer.item.metadata.generated_code ? $t('This code is auto-generated!') : ''"
                    persistent-hint
                  ></v-text-field>
                </v-flex>
                <v-flex class="sm7">
                  <v-text-field v-model="customer.item.name" :label="$t('Name')" :disabled="!isNewWmsOrTpCustomer"
                    :rules="[rules.isValue]"
                  ></v-text-field>
                </v-flex>
                <v-flex class="sm6">
                  <v-text-field v-model="customer.item.phone" type="tel" :rules="vPhone" :hint="$t('Phone BG hint')" :label="$t('Phone')" :disabled="!isNewWmsOrTpCustomer" prepend-inner-icon="phone"></v-text-field>
                </v-flex>
                <v-flex class="sm6">
                  <v-text-field v-model="customer.item.email" :label="$t('Email')" :disabled="!isNewWmsOrTpCustomer" prepend-inner-icon="mail"></v-text-field>
                </v-flex>
                <v-flex class="sm12">
                  <v-text-field v-model="customer.item.address" :label="$t('Address')" :disabled="!isNewWmsOrTpCustomer"></v-text-field>
                </v-flex>
                <v-flex class="sm6">
                  <v-radio-group row :label="$t('Request shipment')"
                    v-model="customer.item.metadata.is_shipment"
                    :rules="[rules.isBooleanValue]"
                  >
                    <v-radio text :value="true"  :label="$t('Yes')"></v-radio>
                    <v-radio text :value="false" :label="$t('Not')"></v-radio>
                  </v-radio-group>
                </v-flex>
                <v-flex class="sm6 text-sm-right">
                  <v-tooltip bottom>
                    <v-btn slot="activator" depressed @click="order" color="success" :disabled="!customer.isFormValid"><!-- FIXME: !! -> ! -->
                      <v-icon left>done</v-icon>
                      {{ $t('Order delivery notes') }}
                    </v-btn>
                    <span>{{ $t('Delivery notes selected') }}</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
            </v-container>
            </v-form>
          </v-card>
        </v-menu>

      </v-toolbar>

      <v-data-table
        :headers="headersShown"
        :items="selectedItems"
        item-key="id"
        :rows-per-page-items="[10, 25, 50]"
        :rows-per-page-text="$t('Rows per page')"
        :no-data-text="$t('Selected for delivery note')"
        class="elevation-1"
        hide-headers
        hide-actions
      >
        <template v-slot:items="props">
          <tr :active="props.item.free_quantity == props.item.quantityInput">
            <td
              v-for="header in headersShown"
              :key="header.value"
              :class="[
                'text-xs-' + header.align,
                header.value.includes('quantity') ? 'blue lighten-5' : '',
                !!props.item.fixed ? 'error--text' : ''
              ]"
            >
              <template v-if="header.value === 'free_quantity'">
                {{ props.item.free_quantity }}
                <span class="grey--text">{{ quantityLeft(props.item) }}</span>
              </template>
              <template v-else-if="header.value === 'wms_station_code'">
                {{ props.item.tp_station_name }}
              </template>
              <template v-else-if="header.value === 'unit_code'">
                {{props.item.unit_code }}
              </template>
              <template v-else-if="header.value === 'exp_date'">
                {{ printDateTime(props.item.exp_date, 1) }}
              </template>
              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="text-xs-right lighten-4 px-1" :class="!!props.item.fixed ? 'red' : 'blue'">
              {{ props.item.quantityInput }}
              <v-tooltip bottom>
                <v-btn slot="activator" icon flat @click="removeSelectedItem(props.item)" color="error">
                  <v-icon>remove_circle</v-icon>
                </v-btn>
                <span>{{ $t('Remove') }}</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>


      <v-snackbar v-model="isCheckResponseAlert" right top multi-line
        :color="isCheckResponseError ? 'error' : 'success'"
        :timeout="isCheckResponseError ? 0 : 5000"
      >
        <span v-if="isCheckResponseError">{{ $t('Some of the products you are ordering, are out of number') }}</span>
        <span v-else>{{ $t('Confirmed available') }}</span>

        <v-btn dark flat @click="isCheckResponseAlert = false">
          {{ $t('Close') }}
        </v-btn>
        <v-btn dark flat @click="processCheckResponse('isFix')" v-if="isCheckResponseError">
          {{ $t('Fix') }}
        </v-btn>
      </v-snackbar>

    </v-card>
    <v-card v-else>
      <v-layout row wrap align-start class="pa-2">
        <v-flex xs11 sm10 offset-sm1>
          <v-text-field
            :value="importedFile && importedFile.name"
            :label="$t('Import filename')"
            readonly persistent-hint
            :hint="$t('Select Excel file template')"
          ></v-text-field>
        </v-flex>
        <v-flex xs1 sm1 class="pt-2">
          <v-tooltip bottom>
            <upload-button slot="activator" id="uploadBtn" :fileChangedCallback="importFile" title="" icon="attach_file" color="transparent" accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"></upload-button>
            {{ $t('Browse') }}
          </v-tooltip>
        </v-flex>
      </v-layout>

      <div v-for="(table, index) in importedItems" :key="index">
        <div class="mt-2 pa-4 layout align-center title">
          {{ $t('Order') }} #{{ index+1 }}: {{ table.wms_station_code }}/{{ table.customer_code }}

          <div v-if="table.errorTypes.size" class="error--text pl-2">{{ Array.from(table.errorTypes).map(error => $t('WMS.IMPORT_ERROR.' + error)).join(', ') }}</div>

          <div class="ml-auto text-no-wrap">
            <v-tooltip bottom>
              <v-btn slot="activator" outline color="success" :disabled="!!table.errorTypes.size" @click="orderImported(index, false)">
                <v-icon left>done</v-icon>
                {{ $t('Deliver notes') }}
              </v-btn>
              <span>{{ $t('Deliver imported notes') }}</span>
            </v-tooltip>
            <v-tooltip bottom v-if="table.items.some(row => !row.order_code)">
              <v-btn slot="activator" outline color="success" :disabled="!!table.errorTypes.size" @click="orderImported(index, true)">
                <v-icon left>done_all</v-icon>
                {{ $t('Deliver notes and Request') }}
              </v-btn>
              <span>{{ $t('Deliver imported notes and request a shipment') }}</span>
            </v-tooltip>
          </div>
        </div>

        <v-data-table
          :headers="importedItemsHeaders"
          :items="table.items"
          item-key="product_code"
          :loading="importedItemsLoading"
          :no-data-text="$t('No data')"
          :no-results-text="$t('No results')"
          class="elevation-1"
        >
          <template #items="{ item }">
            <tr :class="[
              item.errorTypes.size > 0 ? (item.errorTypes.size === 1 ? IMPORT_ERRORS[Array.from(item.errorTypes).toString()].color + ' lighten-5' : 'red') : ''
            ]">
              <td v-for="header in importedItemsHeaders" :key="header.value"
                :class="['text-xs-' + header.align,
                        {'text-no-wrap': header.value === 'errors'},
                        {[header.color]: !!header.color}]"
              >
                <template v-if="header.value === 'errors' && !!item.errorTypes.size">
                  <v-tooltip bottom v-for="error in item.errorTypes" :key="error">
                    <v-icon slot="activator" class="px-1" :color="IMPORT_ERRORS[error].color">{{ IMPORT_ERRORS[error].icon }}</v-icon>
                    {{ $t('WMS.IMPORT_ERROR.' + error) }}
                  </v-tooltip>
                </template>
                <template v-if="!!importedItemError(item[header.value])">
                  <v-chip color="transparent" text-color="error">
                    <v-icon left>error</v-icon>{{ importedItemError(item[header.value]) }}
                  </v-chip>
                </template>
                <template v-else>
                  <template v-if="header.value === 'exp_date'">
                    {{ printDateTime(item.exp_date, 1) }}
                  </template>
                  <template v-else>
                    {{ item[header.value] }}
                  </template>
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>

      <!-- importedItems={{ importedItems }} -->

    </v-card>
  </v-container>
</template>

<script>
import { phoneRules } from "../components/phone";
import { mapGetters, mapActions }                 from 'vuex';
import { wms }                                    from '@/resources';
import { floatTrim, printDateTime, downloadFile } from '@/components/util';
import Loading                                    from '@/components/Loading';
import VSuggest                                   from '@/components/VSuggest';
import VSuggestWmsCustomer                        from '@/components/VSuggestWmsCustomer';
import VInputDateRangePicker                      from '@/components/VInputDateRangePicker';
import UploadButton                               from '@/components/UploadButton';

const ERROR_POPUP_TIMEOUT = 20000;
const CUSTOMER = {
  metadata: {
    is_shipment: undefined,
    // NOTE: new, added on 2025-01-08 for orderImported
    generated_code: undefined,
    steps_customer_id: undefined,
    is_new_wms: undefined,
  },
};
const GROUP_HEADERS = ['name', 'code', 'lot', 'exp_date'];
const FILTER_EXP_DATE_INITIAL = { start_date: undefined, end_date: undefined };

const IMPORT_ERRORS = {
  'CELL_INVALID':              { id: 'CELL_INVALID',              icon: 'error',         color: 'red'         },
  'ORDER_CODE_DIFFER':         { id: 'ORDER_CODE_DIFFER',         icon: 'warning',       color: 'orange'      },
  'ORDER_CODE_IN_OTHER_TABLE': { id: 'ORDER_CODE_IN_OTHER_TABLE', icon: 'warning_amber', color: 'amber'       },
  'ROW_ALREADY_ORDERED':      { id: 'ROW_ALREADY_ORDERED',      icon: 'report',        color: 'light-green' },
};

export default {
  name: 'wms-products',
  props: {
    mode: String
  },
  components: {
    Loading,
    VSuggest,
    VSuggestWmsCustomer,
    VInputDateRangePicker,
    UploadButton
  },
  data() {
    return {
      loading: false,

      msg: {
        type: '',
        text: '',
        time: 5000,
        show: false,
        popup: (text, type) => {
          this.msg.type = type || 'info';
          this.msg.text = text;
          this.msg.time = type === 'error' ? ERROR_POPUP_TIMEOUT : 5000;
          this.msg.show = true;
        }
      },

      headers: [
        { text: this.$t('Code'),        value: 'code',             align: 'left',  sortable: true,  show: true, filter: undefined, persist: true },
        { text: this.$t('Name'),        value: 'name',             align: 'left',  sortable: true,  show: true, filter: undefined },
        { text: this.$t('Warehouse'),   value: 'wms_station_code', align: 'left',  sortable: true,  show: false, nofilter: true },
        { text: this.$t('Lot'),         value: 'lot',              align: 'left',  sortable: true,  show: true, filter: undefined },
        { text: this.$t('Expiry date'), value: 'exp_date',         align: 'left',  sortable: true,  show: true, filter: FILTER_EXP_DATE_INITIAL },
        { text: this.$t('Weight, kg'),  value: 'weight',           align: 'right', sortable: true,  show: false, nofilter: true },
        { text: this.$t('Qty total'),   value: 'total_quantity',   align: 'right', sortable: true,  show: true, filter: 1 },
        { text: this.$t('Qty Locked'),  value: 'blocked_quantity', align: 'right', sortable: false, show: true,  nofilter: true },
        { text: this.$t('Qty free'),    value: 'free_quantity',    align: 'right', sortable: false, show: true,  nofilter: true, persist: true },
        { text: this.$t('Unit'),        value: 'unit_code',        align: 'left',  sortable: false, show: true,  nofilter: true },
      ],
      items: [],
      itemsTotal: 0,
      selectedItems: [],
      pagination: {
        rowsPerPage: 10,
        sortBy: 'name',
        descending: false
      },
      rules: {
        quantityZero: (val) => val >= 0 || this.$t('Quantity cannot be negative'),
        quantityMax: (val, maxlimit) => val >= 0 && val <= maxlimit || (this.$t('Cannot exceed the initial value of ') + maxlimit),
        isQuantityLimits: (val, maxlimit) => val >= 0 && val <= maxlimit,
        msgQuantityLimitsBlocked: item => item.quantityInput > item.free_quantity && item.quantityInput < item.total_quantity ? this.$t('Keep in mind quantity already reserved!') : '',
        isBooleanValue: val => (val === true || val === false) || this.$t('You must choose an option'),
        isValue: val => !!val || this.$t('This field is required!'),
        isValueNotEmpty: val => !!val && val.replaceAll(/\s/gm, '').length >= 3 || this.$t('This field cannot be empty!'),
        isCustomerCodeGenerated: val => val !== this.customer.item.metadata.generated_code || this.$t('This code is auto-generated!')
      },
      // CHECK RESERVED
      checkResponse: null,
      isCheckResponseAlert: false,
      isCheckResponseError: null,
      isCheckAndOrder: false,
      // SUGGESTER
      wmsSuggestWarehouse: wms.suggestWarehouse,
      wmssuggestCustomer: wms.suggestCustomer,//TEST
      // Customer
      customer: {
        item: CUSTOMER,
        isDialog: false,
        isFormValid: false
      },
      floatTrim,
      printDateTime,
      // Import order/products
      // isImported: true,// items source mode
      importedFile: null,
      importedItemsLoading: false,
      importedItemsHeaders: [
        { text: this.$t('Errors')       , value: 'errors'          , align: 'center', color: null },
        { text: this.$t('Warehouse')    , value: 'wms_station_code', align: 'left'  , color: null },
        { text: this.$t('Customer code'), value: 'customer_code'   , align: 'left'  , color: null },
        { text: this.$t('Order code')   , value: 'order_code'      , align: 'left'  , color: 'blue-grey lighten-5' },
        { text: this.$t('Product code') , value: 'product_code'    , align: 'left'  , color: null },
        { text: this.$t('Lot')          , value: 'lot'             , align: 'left'  , color: null },
        { text: this.$t('Expiry date')  , value: 'exp_date'        , align: 'left'  , color: null },
        { text: this.$t('Qty')          , value: 'quantity'        , align: 'right' , color: null },
        { text: this.$t('Line')         , value: 'xls_line'        , align: 'right' , color: 'blue lighten-5', width: '5%' }
      ],
      importedItems: [],
      IMPORT_ERRORS,
      stationFilter: undefined
    }
  },
  computed: {
    vPhone() {
      if (!this.isNewWmsOrTpCustomer) return [];
      return phoneRules(key => this.$t(key), { country: "BG" });
    },
    ...mapGetters(['wmsProductsFilter']),

    pages() {
      if (this.pagination.rowsPerPage == null ||
        this.itemsTotal == null
      ) return 0;

      return Math.ceil(this.itemsTotal / this.pagination.rowsPerPage);
    },

    headersShown() {
      return this.headers.filter(elem => !!elem.show);
    },
    headersWithFilters() {
      return this.headers.filter(elem => !elem.nofilter);
    },

    itemsGroupedCount() {
      let grouped = 0;
      this.items.forEach(item => {
        if (!!item.grouped) {
          grouped += item.grouped;
        }
      });
      return grouped;
    },
    isItemsUnmatched() {
      return this.items.some(elem => !!elem.unmatched);
    },

    wmsStations() {
      let stations = this.items.filter(elem => !!elem.wms_station_code).map(elem => ({
        wms_station_code: elem.wms_station_code,
        tp_station_name: elem.tp_station_name || elem.wms_station_code
      }));
      return [...new Map(stations.map(item => [item['wms_station_code'], item])).values()];
    },

    filtersOn() {
      let filters = {};
      if (!!this.stationFilter) {
        filters.wms_station_code = this.stationFilter;
      }
      this.headers.forEach(elem => {
        if (!!elem.filter) {
          if (elem.value === 'exp_date') {
            ['start_date', 'end_date'].forEach(key2 => {

              if (elem.filter[key2]) {
                filters[key2] = elem.filter[key2];
              }

            });
          }
          else {
            filters[elem.value] = elem.filter;
          }
        }
      });
      return filters;
    },

    isFilterQuantityZero() {
      return !this.headers.find(elem => elem.value === 'total_quantity').filter;
    },

    isNewWmsOrTpCustomer() {
      return this.customer.item.metadata.is_new_wms;
    },

    isImported() {
      return this.$props.mode === 'import';
    }
  },
  methods: {
    ...mapActions(['updateWmsProductsFilter']),


    getItems(isPaginate, isIgnoreLoading) {
      if (this.loading && !isIgnoreLoading) {
        return;
      }
      this.loading = true;

      this.storeFilter();

      let params = this.filtersOn;
      params.group = this.headersShown.filter(header => GROUP_HEADERS.includes(header.value))
                                      .map(header => header.value);

      let { sortBy, descending, page, rowsPerPage } = this.pagination;
      if (!isPaginate) {
        page = 1;
      }

      let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
      let length = (rowsPerPage>=1) ? rowsPerPage : -1;
      let sort   = (sortBy) ? (descending ? '-' : '') + sortBy : '';
      if (sort)
        params = Object.assign({ length, offset, sort }, params);
      else
        params = Object.assign({ length, offset }, params);

      wms.list(params).then(response => {

        this.itemsTotal = response.body.length;
        this.items = this.initializeItemResults(response.body);

      }).catch(response => {

        if (response.body.errors && response.body.errors.find(elem => elem.text && elem.text.startsWith('fromIndex('))) {
          this.getItems(null, 'isIgnoreLoading');
        }
        else {
          this.msg.popup( this.processResponsErr(response, this.$t('Error loading products list')), 'error');
        }

      }).finally(() => {
        this.loading = false;
      });
    },
    initializeItemResults(response_body) {
      let results = JSON.parse(JSON.stringify(response_body.result));
      // response_body.unmatched = ['12INN9REN0', '1AQUAAREN0'];// FIXME: remove line

      return results
        // Set ID
        .map((item, idx) => {
          item.id = ('ofs' + response_body.offset + '_idx' + idx);
          // if (idx === 1) item.grouped = 2;// FIXME: remove line
          if (item.grouped == 1) {
            item.grouped = 0;
          }
          if (response_body.unmatched && response_body.unmatched.includes(item.code)) {
            item.unmatched = true;
          }
          return item;
        })

        // Calculate free quantity and restore selected items
        .map(item => {
          let match = this.selectedItems.find(sitem => sitem.id === item.id);
          item.free_quantity = (Number(item.total_quantity) - Number(item.blocked_quantity));
          if (match) {
            item.quantityInput = match.quantityInput;
          }
          else {
            item.quantityInput = (!!item.free_quantity && Number(item.free_quantity) > 0 ? 0 : null);
          }
          return item;
        });
    },

    columnHideCheck(header) {
      if (header.persist)
        return true;

      if (!header.show)
        return false;

      let v = 0;
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value != 'actions' && this.headers[i].show)
          v++;

      return (v<2);
    },
    columnToggle(header) {
      if (!header.persist) {
        header.show = !header.show;
        this.storeFilter();

        this.getItems();
      }
    },

    changeSort(header) {
      if (!header.sortable)
        return;
      let column = header.value;
      if (this.pagination.sortBy === column) {
        if (this.pagination.descending)
          this.pagination.sortBy = null;
        else
          this.pagination.descending = true;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    toggleFilterQuantityZero() {
      let fq = this.headers.find(elem => elem.value === 'total_quantity');
      fq.filter = fq.filter ? undefined : 1;
      this.getItems();
    },

    storeFilter() {
      let s = {
        pagination:  this.pagination,
        headersdata: this.headers.filter(header => header.value !== 'actions')
          .map(header => {
            return {
              show: !!header.show,
              filter: header.filter
            }
          }),
      };
      this.updateWmsProductsFilter(JSON.stringify(s));
    },

    // ITEMS

    quantityLeft(item) {
      if (item.quantityInput > 0) {
        return ' / ' + (item.free_quantity - item.quantityInput) + ' ' + this.$t('rest');
      }
    },

    clearFilters() {
      this.headers.forEach(elem => {
        if (!elem.nofilter) {
          if (elem.value === 'total_quantity') {
            elem.filter = 1;
          }
          else if (elem.value === 'exp_date') {
            elem.filter = FILTER_EXP_DATE_INITIAL;
          }
          else {
            elem.filter = undefined;
          }
        }
      });
      this.getItems();
    },

    // ORDER (SELECTED ITEMS)

    selectItem(item) {
      let match = this.selectedItems.find(sitem => sitem.id === item.id);
      if (item.quantityInput > 0 && item.quantityInput <= item.free_quantity) {
        if (match) {
          match.quantityInput = item.quantityInput;
          match.fixed = false;
        }
        else {
          this.selectedItems.push({...item});
          this.selectedItems = this.selectedItems.filter(elem => elem.wms_station_code === item.wms_station_code);
        }
        this.checkResponse = null;
      }
      else if (match) {
        const matchIndex = this.selectedItems.findIndex(sitem => sitem.id === item.id);
        this.selectedItems.splice(matchIndex, 1);
      }
    },

    removeSelectedItem(sitemParam) {
      let match = this.items.find(item => item.id === sitemParam.id);
      if (match) {
        match.quantityInput = 0;
        // this.$set(this.items, this.items.findIndex(item => item.id === sitemParam.id), {...match, 'quantityInput': 0});
        console.log('removeSelectedItem', match);
      }
      const matchIndex = this.selectedItems.findIndex(sitem => sitem.id === sitemParam.id);
      this.selectedItems.splice(matchIndex, 1);
    },

    removeSelectedItems(sitems) {
      (sitems || this.selectedItems).forEach(sitem => {
        let match = this.items.find(item => item.id === sitem.id);
        if (match) {
          match.quantityInput = 0;
        }
      });
      this.selectedItems = [];
    },

    //This probably is not necessary as free quantity is already in the table
    checkReserved() {
      if (this.loading || !this.selectedItems.length) {
        return;
      }
      this.loading = true;

      const products = this.selectedItems.map(elem => {
        return {
          product_code: elem.code
        }
      });

      wms.check(products).then(response => {

        this.checkResponse = response.body;
        this.processCheckResponse();

      }).catch(response => {

        this.msg.popup( this.processResponsErr(response, this.$t('Error checking for order availability')), 'error');

      }).finally(() => {
        this.loading = false;
      });

      // setTimeout(() => {// MOCK
      //   let response = {body: [{"product_code":"MIPA","reserved_quantity":55.0}, {"product_code":"120DENREN0","reserved_quantity":50.0}]};
      //   this.checkResponse = response.body;
      //   this.processCheckResponse();
      // }, 1000);
    },


    processCheckResponse(isFix) {
      this.isCheckResponseError = false;

      this.checkResponse.forEach((ritem, idx) => {
        let smatch = this.selectedItems.find(sitem => sitem.code === ritem.product_code);
        if (smatch) {
          let rest = smatch.total_quantity - ritem.reserved_quantity;

          if (rest <= 0 || smatch.quantityInput > rest) {

            this.isCheckResponseError = true;
            smatch.fixed = null;

            if (isFix) {
              const free_quantity = rest > 0 ? rest : 0;

              // Table Selected
              smatch.fixed = true;
              smatch.quantityInput = free_quantity;
              if (rest <= 0) {
                this.removeSelectedItem(smatch);
              }

              // Table Products
              let match = this.items.find(item => item.code === ritem.product_code);
              if (match) {
                match.fixed = true;
                match.quantityInput = free_quantity;
              }
            }
          }
        }
      });

      if (isFix) {
        this.checkResponse = null;
        this.isCheckResponseAlert = false;
        this.isCheckResponseError = false;
      }
      else {
        this.isCheckResponseAlert = true;
      }
    },

    checkAndOrder() {
      this.isCheckAndOrder = true;
      this.checkReserved();
    },

    order() {
      if (!this.$refs.customerForm || !this.$refs.customerForm.validate()) return;
      if (this.loading || !this.selectedItems.length) {
        return;
      }
      this.loading = true;

      const products = this.selectedItems.map(elem => {
        return {
          code:     elem.code,
          quantity: elem.quantityInput,
          lot:      !!elem.grouped && !this.headers.find(header => header.value === 'lot'     ).show ? '' : elem.lot,
          exp_date: !!elem.grouped && !this.headers.find(header => header.value === 'exp_date').show ? '' : elem.exp_date,
        }
      });
      const wms_station_code = this.selectedItems[0].wms_station_code;
      let   customer = JSON.parse(JSON.stringify(this.customer.item));
      const metadata = customer.metadata;
      delete customer.metadata;
// console.log('ordered items=', { products, wms_station_code, customer, metadata });// FIXME: remove line
      wms.order({ products, wms_station_code, customer, metadata }).then(response => {

        this.msg.popup(this.$t('Delivery success'));
        this.removeSelectedItems();
        this.customer.isDialog = false;
        this.getItems();
      }).catch(response => {

        this.msg.popup( this.processResponsErr(response, this.$t('Error creating delivery note')), 'error');

      }).finally(() => {
        this.isCheckAndOrder = false;
        this.loading = false;
      });
    },

    orderImported(index, isShipment) {
      const table = this.importedItems[index];

      if (this.loading || !table.items.length) {
        return;
      }
      this.loading = true;

      const products = table.items.map(elem => {
        return {
          code:     elem.product_code,
          quantity: elem.quantity,
          lot:      elem.lot,
          exp_date: elem.exp_date,
        }
      });
      const wms_station_code = table.wms_station_code || null;
      const customer = {
        code: table.customer_code
      }
      const metadata = {
        ...CUSTOMER.metadata,
        is_shipment: isShipment || false
      }
      const order_code = table.items[0].order_code;

// console.log('ordered imported items=', { products, wms_station_code, customer, metadata });// FIXME: remove line
      wms.order({ products, wms_station_code, customer, metadata, order_code }).then(response => {

        this.msg.popup(this.$t('Delivery success'));

      }).catch(response => {

        this.msg.popup( this.processResponsErr(response, this.$t('Error creating delivery note')), 'error');

      }).finally(() => {
        this.loading = false;
      });
    },

    wmsCustomerSuggesterChange(evt) {
      if (!evt) {
        this.customer.item = CUSTOMER;
        return;
      }
      const smodel = JSON.parse(JSON.stringify(evt));
      console.log('wmsCustomerSuggesterChange', smodel);

      this.customer.item = {
        ...smodel.customer,
        metadata: Object.assign(this.customer.item.metadata, smodel.metadata),
        code: (smodel.customer && smodel.customer.code) || smodel.metadata.generated_code
      };

      if (this.isNewWmsOrTpCustomer) {
        this.customer.item.metadata.is_shipment = null;
      }
    },

    /** Process WMS errors[] response. {responserr} response.body.errors{path:"", text:null}[] */
    processResponsErr(response, errorText) {
      let text = '';
      let responserr = response.body.errors || response.body.error;
      if (responserr && Array.isArray(responserr) && responserr.some(elem => elem.text !== null)) {
        text = responserr.map(elem => elem.text).join(', ');
      }
      else {
        text  = errorText || this.$t('Error');
      }
      return text;
    },

    exportExcel() {
      if (this.loading)
        return;

      this.loading = true;

      let//
      params = this.filtersOn;

      wms.xlsProducts(params).then(response => {
        downloadFile(response);
      }, error => {
        this.msg.popup(error.statusText, 'error');
      }).finally(() => {
        this.loading = false;
      });
    },

    // Import products
    importFile(evt) {
      this.importedFile = evt || null;
      if (evt) {
        this.importFileProcess();
        // this.cleanup();
      }
    },
    importFileProcess() {
      this.$http.post('wms-import-order.svc', this.importedFile, { headers: { 'Content-Type': 'application/vnd.ms-excel' } }).then(response => {
        this.importedItems = this.setErrors(response.body);
      }).catch(error => {
        console.log(error);
        this.msg.popup( error, 'error');
      });
    },
    importedItemError(value) {
      // console.log('importedItemError item=', value);
      if (value === null || value === undefined) {
        return false;
      }
      const errStr = 'err_';
      const str = value.toString();
      if (str.startsWith(errStr)) {
        return str.substring(errStr.length);
      }
      else {
        return false;
      }
    },
    setErrors(tables) {
      tables.forEach((table, index) => {
        if (!table.errorTypes) {
          table.errorTypes = new Set();
        }

        // order_code not same in all rows
        if (table.order_code_differ) {
          table.errorTypes.add('ORDER_CODE_DIFFER');// ORDER_CODE_INCONSISTENT
        }

        table.items.forEach(row => {
          if (!row.errorTypes) {
            row.errorTypes = new Set();
          }

          // Row already imported
          if (table.ordered.includes(row.xls_line)) {
            table.errorTypes.add('ROW_ALREADY_ORDERED');
            row.errorTypes.add('ROW_ALREADY_ORDERED');
          }

          Object.keys(row).forEach(col => {

            // Cell error
            if (!!this.importedItemError(row[col])) {
              table.errorTypes.add('CELL_INVALID');
              row.errorTypes.add('CELL_INVALID');
            }

            // order_code exists also in other table
            if (col === 'order_code') {
              tables.forEach((nextTable, nextTableIndex) => {
                if (nextTableIndex > index) {
                  nextTable.items.forEach(nextTableRow => {
                    if (nextTableRow.order_code === row.order_code) {

                      table.errorTypes.add('ORDER_CODE_IN_OTHER_TABLE');
                      row.errorTypes.add('ORDER_CODE_IN_OTHER_TABLE');

                      if (!nextTable.errorTypes) {
                        nextTable.errorTypes = new Set();
                      }
                      if (!nextTableRow.errorTypes) {
                        nextTableRow.errorTypes = new Set();
                      }
                      nextTable.errorTypes.add('ORDER_CODE_IN_OTHER_TABLE');
                      nextTableRow.errorTypes.add('ORDER_CODE_IN_OTHER_TABLE');
                    }
                  });
                }
              });
            }
          });
        })
      });
      return tables;
    }
  },
  watch: {
    pagination: {
      handler() {
        this.getItems('isPaginate');
      },
      deep: true
    },
    isCheckResponseAlert: function() {
      if (this.isCheckAndOrder) {
        this.order();
      }
    }
  },
  mounted() {
    {// reStore
      let storedFilter = null;
      try {
        storedFilter = JSON.parse(this.wmsProductsFilter);
      } catch(e) {
        console.log('Filter cannot be parsed', e);
        storedFilter = null;
      }

      if (storedFilter) {
        storedFilter.headersdata.forEach((elem, idx) => {
          if (typeof elem.show !== 'undefined') {
            this.headers[idx].show = elem.show;
          }
          if (typeof elem.filter !== 'undefined') {
            this.headers[idx].filter = elem.filter;
          }
        });

        if (storedFilter.pagination) {
          this.pagination = storedFilter.pagination;
        }
      }
    }

    // this.items = this.initializeItemResults(RESPONSE);// MOCK

  }
}
</script>

<style scoped>
.input-right >>> input {
  text-align: right;
}
.pagination >>> button {
  box-shadow: none;
}
[class*='col-'] {
  white-space: normal;
}
.col-free_quantity,
.col-input_quantity {
  min-width: 9rem;
}
[class*='col-'] > .v-input {
  min-width: 10rem
}
</style>